<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsModal from '@/components/pds/PdsModal.vue';
import { computed, ref } from 'vue';
import { formatPickup, formatMoney } from '../format';
import type { PdsCancellation, PdsOrder } from '../types';
import AmountRow from './AmountRow.vue';
import ClientMessageField from './ClientMessageField.vue';
import InfoNotice from './InfoNotice.vue';
import ReasonPicker from './ReasonPicker.vue';

// Refuser une commande à accepter et annuler une commande en cours sont le même geste : même écran.
const props = defineProps<{
  order: PdsOrder;
  busy?: boolean;
  kind?: 'cancel' | 'refuse';
}>();
const refusing = computed(() => props.kind === 'refuse');
const emit = defineEmits<{
  close: [];
  confirm: [cancellation: PdsCancellation];
}>();

const reasons = ref<string[]>([]);
const message = ref('');

const confirm = () => {
  if (props.busy || !reasons.value.length) return;
  emit('confirm', { reasons: reasons.value, message: message.value.trim() });
};
</script>

<template>
  <PdsModal size="lg" :dismissible="!busy" @close="emit('close')">
    <template #title>
      {{ refusing ? 'Refuser la commande' : 'Annuler la commande' }}
    </template>
    <template #subtitle>
      #{{ order.orderNumber ?? order.number }} · {{ order.label }} · retrait
      {{ formatPickup(order.dueAt) }}
    </template>

    <div class="flex flex-col gap-5 p-5">
      <div class="flex flex-col gap-3">
        <p
          class="text-encre-douce text-[12px] font-semibold tracking-[0.9px] uppercase"
        >
          Motif · au moins un
        </p>
        <ReasonPicker v-model="reasons" :disabled="busy" />
      </div>

      <div class="bg-creux flex flex-col gap-1 rounded-[24px] px-3 pt-3 pb-2">
        <div class="flex items-baseline justify-between gap-3 px-1">
          <p
            class="text-encre-douce text-[12px] font-semibold tracking-[0.9px] uppercase"
          >
            Note lue par le client
          </p>
          <p class="text-encre-douce text-[12px]">
            visible sur sa page de commande
          </p>
        </div>
        <ClientMessageField
          v-model="message"
          :placeholder="`Expliquez-lui pourquoi la commande est ${refusing ? 'refusée' : 'annulée'}`"
        />
      </div>

      <AmountRow
        label="Remboursement intégral"
        :amount="formatMoney(order.totalPaidCents)"
      />

      <InfoNotice title="Le client sera prévenu">
        Le motif n'est pas envoyé au client : seule la note ci-dessus lui est
        lue, sur sa page de commande.
      </InfoNotice>
    </div>

    <template #footer>
      <div class="flex w-full items-center gap-3 sm:gap-4">
        <PdsButton variant="second" fit :disabled="busy" @click="emit('close')">
          Retour
        </PdsButton>
        <span class="text-encre-douce ml-auto text-[12px] max-sm:hidden">
          {{
            reasons.length
              ? 'Irréversible · le remboursement part aussitôt'
              : 'Cochez au moins un motif'
          }}
        </span>
        <PdsButton
          class="max-sm:flex-1"
          fit
          :loading="busy"
          :disabled="!reasons.length"
          @click="confirm"
        >
          {{ refusing ? 'Confirmer le refus' : "Confirmer l'annulation" }}
        </PdsButton>
      </div>
    </template>
  </PdsModal>
</template>
